"use client";

import { Popover as BasePopover } from "@base-ui/react/popover";
import clsx from "clsx";
import React, { useContext, useRef } from "react";
import type { CommonHtmlProps } from "shared/UtilTypes";
import {
  FLOATING_POPUP_CLASS,
  type FloatingPlacement,
  type FloatingPositioningProps,
  getFloatingPopupStyle,
  getFloatingPortalProps,
  getFloatingPositionerProps,
} from "shared-web/components/common/FloatingLayer/FloatingLayer";
import { composeRefs } from "src/components/common/Composition";
import { VisibilityProvider } from "src/lib/Visibility";
import { rendersNativeButton } from "./NativeButton";
import PopoverContext, {
  getFinalFocus,
  type PopoverContextValue,
  useOverlayState,
} from "./PopoverContext";

const DEFAULT_TRIGGER: TriggerType[] = ["hoverIn", "hoverOut", "focus", "blur"];

export type TriggerType = "hoverIn" | "hoverOut" | "focus" | "click" | "blur" | "keyboard";
export type PopoverHandle = PopoverContextValue;

export type VisibleChangeReason = string;

export type PopoverPlacement = FloatingPlacement;
export type PopoverPositioningProps = FloatingPositioningProps;

export interface PopoverProps extends PopoverPositioningProps {
  visible?: boolean;
  interactive?: boolean;
  onVisibleChange?: (value: boolean, event?: Event, reason?: VisibleChangeReason) => void;
  children?: React.ReactNode | ((state: PopoverContextValue) => React.ReactNode);
  disabled?: boolean;
  triggers?: Array<TriggerType | false | null | undefined>;
  handleRef?: React.Ref<PopoverHandle>;
  overlay?: boolean;
  animated?: boolean;
  hoverDelay?: number;
  tags?: string[];
}

const isKeyboardPress = (event: Event | undefined) =>
  event instanceof KeyboardEvent || (event instanceof MouseEvent && event.detail === 0);

interface PopoverInternalContextValue {
  triggers: TriggerType[];
  hoverDelay: number;
  disabled: boolean;
  animated: boolean;
  fillWidth: boolean;
  positionerProps: ReturnType<typeof getFloatingPositionerProps>;
  openReasonRef: React.RefObject<TriggerType | null>;
  /** Set when the popover closes with focus inside it, so focus returning to the trigger does not reopen it. */
  ignoreNextFocusRef: React.RefObject<boolean>;
  openFrom: (type: TriggerType, event: Event) => void;
  closeFrom: (type: TriggerType, event: Event) => void;
}

const PopoverInternalContext = React.createContext<PopoverInternalContextValue | undefined>(
  undefined,
);

const Popover = (props: PopoverProps) => {
  const {
    disabled = false,
    onVisibleChange,
    handleRef,
    hoverDelay,
    children,
    triggers,
    animated = true,
    tags,
    fillWidth = false,
    visible: propsVisible,
  } = props;
  const openReasonRef = useRef<TriggerType | null>(null);
  const ignoreNextFocusRef = useRef(false);
  const resolvedTriggers = (triggers ?? DEFAULT_TRIGGER).filter(Boolean) as TriggerType[];
  const overlay = useOverlayState({
    visible: propsVisible,
    onVisibleChange: (nextVisible, event, reason) => {
      if (typeof document !== "undefined") {
        // Closing restores focus to the trigger, which must not count as a new focus open.
        ignoreNextFocusRef.current =
          !nextVisible &&
          Boolean(overlay.context.refs.floating.current?.contains(document.activeElement));
      }
      onVisibleChange?.(nextVisible, event, reason);
    },
    handleRef,
    tags,
  });
  const { visible, context } = overlay;

  const setVisible = (nextVisible: boolean, event?: Event, reason?: VisibleChangeReason) => {
    if (!nextVisible) openReasonRef.current = null;
    overlay.setVisible(nextVisible, event, reason);
  };

  const isAllowed = (type: TriggerType) =>
    resolvedTriggers.includes(type) ||
    // Keyboard activation follows mouse activation, as floating-ui's click handler did.
    (type === "keyboard" && resolvedTriggers.includes("click"));

  const openFrom = (type: TriggerType, event: Event) => {
    if (disabled || visible || !isAllowed(type)) return;
    openReasonRef.current = type;
    setVisible(true, event, type);
  };

  const closeFrom = (type: TriggerType, event: Event) => {
    if (!visible || !isAllowed(type)) return;
    setVisible(false, event, type);
  };

  const handleOpenChange = (nextVisible: boolean, details: BasePopover.Root.ChangeEventDetails) => {
    const event = details.event as Event | undefined;
    const openReason = openReasonRef.current;
    let type: TriggerType | undefined;
    switch (details.reason) {
      case "trigger-press":
        type = isKeyboardPress(event) ? "keyboard" : "click";
        // A press keeps a popover open that hover or focus opened, and then owns it.
        if (!nextVisible && (openReason === "hoverIn" || openReason === "focus")) {
          if (isAllowed(type)) openReasonRef.current = type;
          return;
        }
        break;
      case "trigger-hover":
        type = nextVisible ? "hoverIn" : "hoverOut";
        // Leaving the trigger does not close a popover that was opened by a press.
        if (!nextVisible && (openReason === "click" || openReason === "keyboard")) return;
        break;
      case "trigger-focus":
        type = nextVisible ? "focus" : "blur";
        break;
      case "outside-press":
      case "focus-out":
        type = "blur";
        break;
    }
    if (type && !isAllowed(type)) return;
    if (nextVisible && disabled) return;
    if (nextVisible) openReasonRef.current = type ?? null;
    setVisible(nextVisible, event, details.reason);
  };

  return (
    <PopoverContext.Provider value={context}>
      <PopoverInternalContext.Provider
        value={{
          triggers: resolvedTriggers,
          hoverDelay: hoverDelay ?? 100,
          disabled,
          animated,
          fillWidth,
          positionerProps: getFloatingPositionerProps(props),
          openReasonRef,
          ignoreNextFocusRef,
          openFrom,
          closeFrom,
        }}
      >
        <BasePopover.Root
          open={visible}
          onOpenChange={handleOpenChange}
          onOpenChangeComplete={(open: boolean) => {
            overlay.onOpenChangeComplete(open);
            // Focus is back on the trigger by now. If it went elsewhere, don't swallow the next focus.
            if (!open) {
              requestAnimationFrame(() => {
                ignoreNextFocusRef.current = false;
              });
            }
          }}
        >
          {typeof children === "function" ? children(context) : children}
        </BasePopover.Root>
      </PopoverInternalContext.Provider>
    </PopoverContext.Provider>
  );
};

export type PopoverReferenceProps<C extends React.ElementType = "button"> =
  React.ComponentProps<C> & {
    as?: C;
    ref?: React.Ref<C>;
    asChild?: boolean;
    trigger?: boolean;
  };

const PopoverReference = <C extends React.ElementType = "button">(
  props: PopoverReferenceProps<C>,
) => {
  const { asChild, trigger, ref, as, children, ...otherProps } = props;
  const popoverContext = useContext(PopoverContext);

  if (trigger === false) {
    const Comp: any = as ?? "button";
    const element = asChild && React.isValidElement(children) ? children : <Comp>{children}</Comp>;
    return React.cloneElement(element as React.ReactElement<any>, {
      ...otherProps,
      "data-popover-reference": true,
      ref: composeRefs(ref as React.Ref<Element>, popoverContext?.refs.reference),
    });
  }

  return (
    <PopoverTrigger
      asChild={asChild}
      as={as}
      ref={ref}
      data-popover-reference={true}
      {...otherProps}
    >
      {children}
    </PopoverTrigger>
  );
};

export type PopoverTriggerProps<C extends React.ElementType = "button"> =
  React.ComponentProps<C> & {
    as?: C;
    ref?: React.Ref<any>;
    asChild?: boolean;
  };

const PopoverTrigger = <C extends React.ElementType = "button">(props: PopoverTriggerProps<C>) => {
  const { asChild, ref, as, children, onFocus, onBlur, ...otherProps } = props;
  const popoverContext = useContext(PopoverContext);
  const internal = useContext(PopoverInternalContext);
  const Comp: any = as ?? "button";
  const render =
    asChild && React.isValidElement(children) ? (children as React.ReactElement) : <Comp />;
  const hoverEnabled =
    !internal?.disabled &&
    Boolean(internal?.triggers.includes("hoverIn") || internal?.triggers.includes("hoverOut"));

  return (
    <BasePopover.Trigger
      ref={composeRefs(ref, popoverContext?.refs.reference)}
      data-popover-trigger={true}
      render={render}
      nativeButton={rendersNativeButton(asChild ? children : undefined, Comp)}
      openOnHover={hoverEnabled}
      delay={internal?.hoverDelay}
      onFocus={(event: React.FocusEvent<HTMLElement>) => {
        onFocus?.(event);
        let focusVisible = true;
        try {
          focusVisible = event.currentTarget.matches(":focus-visible");
        } catch {}
        if (internal?.ignoreNextFocusRef.current) {
          internal.ignoreNextFocusRef.current = false;
          return;
        }
        if (focusVisible) internal?.openFrom("focus", event.nativeEvent);
      }}
      onBlur={(event: React.FocusEvent<HTMLElement>) => {
        onBlur?.(event);
        const next = event.relatedTarget as Node | null;
        if (next && popoverContext?.refs.floating.current?.contains(next)) return;
        internal?.closeFrom("blur", event.nativeEvent);
      }}
      {...otherProps}
    >
      {asChild ? undefined : children}
    </BasePopover.Trigger>
  );
};

export type PopoverContentProps = CommonHtmlProps & {
  asChild?: boolean;
  /** Whether opening moves focus into the popup. Defaults to moving it only for press opens. */
  initialFocus?: boolean;
};

const PopoverContent = (props: PopoverContentProps) => {
  const { children, ref, asChild, className, style, initialFocus, ...otherProps } = props;
  const popoverContext = useContext(PopoverContext);
  const internal = useContext(PopoverInternalContext);

  if (!popoverContext || !internal) {
    return null;
  }

  return (
    <BasePopover.Portal {...getFloatingPortalProps(popoverContext.refs.reference.current)}>
      <BasePopover.Positioner {...internal.positionerProps}>
        <BasePopover.Popup
          ref={composeRefs(ref, popoverContext.refs.floating)}
          id={popoverContext.floatingId}
          finalFocus={getFinalFocus(popoverContext)}
          initialFocus={() => {
            if (initialFocus !== undefined) return initialFocus;
            const reason = internal.openReasonRef.current;
            return reason === "click" || reason === "keyboard";
          }}
          render={(popupProps) => {
            const dataProps = { "data-popover-animated": internal.animated };
            if (asChild && React.isValidElement(children)) {
              const { children: _, ...childProps } = popupProps;
              return React.cloneElement(children as React.ReactElement<any>, {
                ...childProps,
                ...dataProps,
              });
            }
            return <div {...popupProps} {...dataProps} />;
          }}
          className={clsx(FLOATING_POPUP_CLASS, "no-focus-ring", className)}
          style={{ ...getFloatingPopupStyle(internal.fillWidth), ...style }}
          {...(otherProps as any)}
        >
          {asChild ? undefined : (
            <VisibilityProvider value={{ visible: popoverContext.presented }}>
              {children}
            </VisibilityProvider>
          )}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
};

Popover.Reference = PopoverReference;
Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;

export default Popover;
